import {ChangeEvent} from 'react';
import {Ad, AdSet, Campaign} from 'titan-ads/lib/shapes/TitanShapes';
import {SuspenseStatus, TimeRangeProps} from './types';
import {NodeShape} from 'lincd/lib/shapes/SHACL';
import {Server} from 'lincd-server-utils/lib/utils/Server';
import {packageName} from './package';
import {ExecutableAction} from 'titan-ads/lib/shapes/ExecutableAction';

export const obsCachetime = 15 * 60 * 1000; // 15 minutes
export type QueryFilter = { 
  field?: string; 
  shapeField?: string, 
  value?: any, 
  fn?: (instance,{startDate, endDate, isToday}:{startDate:Date,endDate:Date,isToday:boolean})=>boolean,
  /**
   * used for caching, needs to be unique for the function _and_ any fixed parameters
   */
  fnName?: string,
  operator?: string
};
export interface TitanQueryConfig {
  shape: NodeShape;
  search?: string;
  /**
   * ignore cache and generate the result from scratch
   */
  forceRefresh?:boolean;
  limit?: number;
  offset?: number;
  exportData?: boolean;
  sortOrder?: string;
  sortBy?: {
    id: string;
    desc: boolean;
  };
  filters?: QueryFilter[];
  timeRange?: TimeRangeProps;
  timeRangeProperty?: string;
  /**
   * the response should include information about the columns (human readable labels and tooltips)
   * Should be true on the first request, false afterwards if the column info is already stored in state
   */
  includeColumns?:boolean;
  targetDate?: Date|string; //string is used during transport to ensure compatibility with JSON serialization.
  /**
   * number of milliseconds to cache the data for
   */
  cacheTime?: number;
}

export interface ExportQueryResult {
  instances: any[];
  columns?: Array<string | { key: string; label: string }>;
}

export const prefixedLog = (prefix,...messages:any[]) => {
  return logger(prefix,console.log,...messages);
}
export const prefixedError = (prefix,...messages:any[]) => {
  return logger(prefix,console.error,...messages);
}
const logger = (prefix,logFn,...messages:any[]) => {
  messages = messages.map(m => prefix+m.toString());
  try {
    logFn(...messages)
    return messages.join("\n")+ "\n";
  } catch (error) {
    console.error(error);
  }
}

let timer;
export function debounce(func, timeout = 700) {
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), timeout);
  };
}

export function useSuspense(promise: Promise<any>) {
  let status: SuspenseStatus = 'pending';

  let result;
  const suspender = promise.then(
    (r) => {
      status = 'success';
      result = r;
    },
    (e) => {
      status = 'error';
      result = e;
    },
  );

  return {
    read() {
      switch (status) {
        case 'error':
        case 'pending':
          throw suspender;
        case 'success':
          return result;
      }
    },
  };
}
export type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';

export interface DMYQ {
  DAY: string;
  MONTH: string;
  QUARTER: Quarter;
  YEAR: string;
}

/**
 * Get the day, month, and year of a date.
 * @param date
 */
export function getDMYQ(date:string): DMYQ {
  //parse the date (YYYY-MM-DD)
  const dateParts = date.split('-');
  const YEAR = dateParts[0];
  const MONTH = dateParts[1];
  const DAY = dateParts[2];
  // const YEAR = date.getFullYear().toString();
  // const MONTH = zeroPad(date.getMonth() + 1, 2);
  // const DAY = zeroPad(date.getDate(), 2);

  const QUARTER: Quarter = getQuarter(parseInt(MONTH));

  return {DAY, MONTH, QUARTER, YEAR};
}

/**
 * Being used for file creation, ensuring dates
 * are padded with a leading `0` to a certain length:
 *
 * ```
 * zeroPad(5, 2)  // returns "05"
 * zeroPad(12345, 3)  // returns "1234"
 * ```
 */
export function zeroPad(num: number, maxPad: number): string {
  return `${num}`.padStart(maxPad, '0');
}

export function getQuarter(month: number): Quarter {
  return `Q${Math.ceil(month / 3)}` as Quarter;
}

export function getDatesBetweenDates(startDate: string, endDate: string): string[] {
  //dates are manual string dates of the format "YYYY-MM-DD"
  //we want to manuall create a list of these sort of string dates between these two dates
  const dates = [];
  //parse year,month,day
  const start = startDate.split('-');
  const end = endDate.split('-');
  //parse year,month,day
  const startYear = parseInt(start[0]);
  const endYear = parseInt(end[0]);
  const startMonth = parseInt(start[1]);
  const endMonth = parseInt(end[1]);
  const startDay = parseInt(start[2]);
  const endDay = parseInt(end[2]);
  //iterate from the start year to the end year
  for (let y = startYear; y <= endYear; y++)
  {
    //iterate from the start month to the end month
    for (let m = startMonth; m <= endMonth; m++)
    {
      //iterate from the start day to the end day
      for (let d = startDay; d <= endDay; d++)
      {
        //add the date to the list of dates
        dates.push(`${y}-${zeroPad(m,2)}-${zeroPad(d,2)}`);
      }
    }
  }
  return dates;
}


// Looks up existing campaigns, if one with the same name exists, then
// increment the unique identifier
export function generateUniqueCampaignTitle(
  campaigns: Campaign[],
  campaignName: string,
): string {
  const suffix: number = campaigns.filter((c) => {
    const startsWithSameCampaignName = c.name.startsWith(campaignName);

    // Need to do an additional check for special cases where an account
    // name might be equal to the beginning of a string, but have more to
    // it.  For example:
    //
    // alreadyExists = ["a b cdef 1", "a b c 1"]
    // toCreate = "a b c"
    //
    // We want to match cases where everything except the last number is
    // part of the campaign title
    if (startsWithSameCampaignName) {
      const unmatchedIsNumber = !Number.isNaN(
        c.name.replace(campaignName, '').trim(),
      );

      return unmatchedIsNumber;
    }
  }).length;

  return `${campaignName} ${suffix + 1}`;
}

/**
 * Retrieves the currently selected option's value of a given attribute on a
 * HTMLSelectElement
 * @param e The element event from which the attribute should be retrieved
 * @param attr The name of the attribute attached to the HTML element
 * @returns The value given to `attr` on the HTML element
 */
export function getAttribute(e: ChangeEvent<HTMLSelectElement>, attr: string) {
  return e.target[e.target.selectedIndex].getAttribute(attr);
}

/**
 * Unique-ify adset and campaign results
 */
export function dedupe(withDuplicates: any[] = [], callback): any {
  const seen = {};

  return withDuplicates.filter((item) => {
    const prop = callback(item);

    return seen.hasOwnProperty(prop) ? false : (seen[prop] = true);
  });
}

/**
 * AdSet prefixes are in the form "XY-aZ09", and we're interested in
 * extracting the "aZ09" part of the prefix
 * @param adSetPrefix
 * @returns
 */
export function extractIdFromAdSetPrefix(adSetPrefix: string) {
  return adSetPrefix.split('-')[1];
}

export const getShapeNameFromParams = (str) => {
  //example get Person from schema:person

  const lastWord = str.split(':').pop();
  const capitalizedLastWord =
    lastWord.charAt(0).toUpperCase() + lastWord.slice(1);
  return capitalizedLastWord;
};

export const getShapeParamsFromUri = (str) => {
  //example get schema:person from https://data.lincd.org/module/lincd-schema/shape/person

  // Get the word between "module" and "/shape"
  const betweenLincdAndShape = str.match(/module\/(.*?)\/shape/);
  const wordBetween = betweenLincdAndShape ? betweenLincdAndShape[1] : null;

  // Get the last word after the last "/"
  const lastWord = str.substring(str.lastIndexOf('/') + 1);

  return wordBetween + ':' + lastWord;
};

export function loadTrendReports(brandAccountUri) {
  return Server.call(packageName, 'loadTrendReports', brandAccountUri)
    .then((data) => {
      return data;
    })
    .catch((err) => {
      console.error('Error loading trend reports: ', err);
      throw err;
    });
}

interface LoadExecutableActionsParams {
  searchQuery: string;
  pageSize: number;
  pageIndex: number;
  sorting: TitanQueryConfig['sortBy'][];
  actionStatus?: number; // Optional parameter for filtering by status
  uri?: string;
  filters?: TitanQueryConfig['filters'];
  timeRange?: TimeRangeProps;
  timeRangeProperty?: string;
}

export function loadExecutableAction({
  searchQuery,
  pageSize,
  pageIndex,
  sorting,
  actionStatus = 0, // Default status to 0 (for potential status)
  uri, 
  filters,
  timeRange,
  timeRangeProperty
}: LoadExecutableActionsParams) {
   return Server.call(packageName,'loadApprovalData',{
    shape: ExecutableAction.shape,
    search: searchQuery,
    limit: pageSize, // For example, limit to 10 items
    offset: pageIndex * pageSize, // Start from the first item
    sortBy: sorting.length > 0 ? sorting[0] : undefined,
    filters: filters || [],
    timeRange,
    timeRangeProperty
  } as TitanQueryConfig)
    .then((data) => {
      return {instances: data.instances, numInstances: data.numInstances};
    })
    .catch((err) => {
      console.error('Error load executable action data: ', err);
      throw err;
    });
}

export function loadApprovalDataObjects(uri) 
{
  return Server.call(packageName, 'loadApprovalDataObjects', uri)
        .then(data => {
          return data;
        }).catch(err => {
          console.error('Error load executable action objects: ', err);
          throw err;
        })
}

interface LoadDataProps {
  searchQuery: string;
  pageSize: number;
  pageIndex: number;
  sorting: TitanQueryConfig['sortBy'][];
  filters?: QueryFilter[];
  targetDate?: Date
  timeRange?: TimeRangeProps;
  includeColumns?:boolean;
  exportData?: boolean;
}

export interface LoadAdDataResponse {
  instances: any[];
  numInstances: number;
  columns?: any[];
  exportPath?: string; 
}

export function loadAdData({
  searchQuery,
  pageSize,
  pageIndex,
  sorting,
  filters,
  timeRange,
  includeColumns,
  exportData,
}: LoadDataProps): Promise<LoadAdDataResponse> {
  return Server
    .call(packageName, 'loadAdManagementData', {
      shape: Ad.shape,
      search: searchQuery,
      limit: pageSize,
      offset: pageIndex * pageSize,
      sortBy: sorting.length ? sorting[0] : undefined,
      filters: filters?.length ? filters : undefined,
      timeRange,
      includeColumns,
      exportData,
    } as TitanQueryConfig)
    .then(data => {
      if (!data || data.exportPath) {
        return { instances: [], numInstances: 0, columns: [], exportPath: data?.exportPath };
      }
      return {
        instances: data.instances,
        numInstances: data.numInstances,
        columns: data.columns,
      };
    });
}

export function loadAdSetData({
  searchQuery,
  pageSize,
  pageIndex,
  sorting,
  filters,
  timeRange,
  includeColumns
}: LoadDataProps) {
  return Server.call(packageName, 'loadAdSetManagementData', {
    shape: AdSet.shape,
    search: searchQuery,
    limit: pageSize,
    offset: pageIndex * pageSize,
    filters: filters ? filters : undefined,
    sortBy: sorting.length > 0 ? sorting[0] : undefined,
    includeColumns,
    timeRange,
  } as TitanQueryConfig).then(data => {
    return { 
      instances: data.instances, 
      numInstances: data.numInstances,
      columns: data.columns
    };
  });
}

export function loadCampaignData({
  searchQuery,
  pageSize,
  pageIndex,
  sorting,
  filters,
  targetDate,
  includeColumns
}: LoadDataProps) {
  return Server.call(packageName, 'loadCampaignManagementData', {
    shape: Campaign.shape,
    search: searchQuery,
    limit: pageSize,
    offset: pageIndex * pageSize,
    filters: filters ? filters : undefined,
    sortBy: sorting.length > 0 ? sorting[0] : undefined,
    targetDate: targetDate ? targetDate.toISOString() : undefined, //Serialize targetDate using toISOString() before sending it in the payload
    includeColumns
  } as TitanQueryConfig).then((data) => {
    return {
      instances: data.instances, 
      numInstances: data.numInstances,
      columns: data.columns
    };
  });
}

export interface DynamicObject {
  [key: string]: any;
}

export interface CustomColumn {
  meta?:any;//className?:(row:DynamicObject)=>string;
  // property?: {label?: string};
  label: string;
  id:string;
  renderCell: (row: any) => any; // Using 'any' for the row parameter
  info?: string; //for tooltip
  sticky?: boolean;
  hoverflow?: boolean;
  preWrap?: boolean; //Text will wrap when necessary, and on line breaks
}

import {ColumnDef} from '@tanstack/react-table';
import { TableColumn } from './utils/views/TableDataProvider';
// Extend ColumnDef to include the tooltip property
export type CustomColumnDef<TData = any> = ColumnDef<TData> & {
  tooltip?: string;
  sticky?: boolean;
  hoverflow?: boolean;
  preWrap?: boolean; //Text will wrap when necessary, and on line breaks
};

// Reusable function to generate columns
// export const generateCustomColumns = (
//   instances: DynamicObject[],
// ): CustomColumn[] => {
//   const allKeys = new Set<string>();

//   instances.forEach((instance) => {
//     Object.keys(instance).forEach((key) => {
//       if (key !== 'id') {
//         allKeys.add(key);
//       }
//     });
//   });

//   return Array.from(allKeys).map((key) => ({
//     label: key,
//     // property: {
//     //   label: key
//     //     .replace(/([a-z])([A-Z])/g, '$1 $2')
//     //     .replace(/^./, (str) => str.toUpperCase()),
//     // },
//     renderCell: (row: DynamicObject) => {
//       const value = row[key];
//       return typeof value === 'object' && value !== null ? value.uri : value;
//     },
//   }));
// };

export const renderNoDataMessage = (
  instances: DynamicObject[],
  statusType: string,
) => {
  if (instances && instances.length === 0) {
    return <h1>No {statusType}</h1>;
  }
  return null;
};

export function yyyyMMddToMMddyy(date: string) {
  const [year, month, day] = date.split('-');

  return `${month}/${day}/${year.slice(2)}`;
}

/**
 * check if a date is today. 
 * if now is provided, it will be used as the current date, and possibly use quoraTime.targetDate
 * 
 * @param date 
 * @param today_DDMMYYY
 * @returns boolean
 */
export function isDateToday(date: Date | undefined, today_DDMMYYY: string = ''): boolean {
  if (!date) return false;
  return date.toISOString().split('T')[0] === today_DDMMYYY;
}
